import React, { useState, useEffect, useRef } from "react";
import "../../styles/sidebar/ScanRoom.css";
import { useMamdani } from "../../context/MamdaniContext";

const API      = "https://api.anti-bot.ai";
const API_SCAN = "https://api.anti-bot.ai";

const EVENT_ICONS = {
  check:        "🔍",
  check_ok:     "✅",
  fetch_start:  "🌐",
  tweet_found:  "📝",
  replies_page: "💬",
  users_fetch:  "👥",
  next_page:    "⏭️",
  rate_limit:   "⏳",
  fetch_ok:     "✅",
  fetch_error:  "❌",
  analyze:      "🤖",
  analyze_ok:   "✅",
  error:        "❌",
  done:         "🎯",
  geocode:      "🗺️",
};

function ReplyCard({ reply, index }) {
  const isBot    = reply.is_bot;
  const initials = (reply.username || "?")[0].toUpperCase();
  return (
    <div
      className={`sr-reply-card ${isBot ? "is-bot" : ""}`}
      style={{ animationDelay: `${index * 0.03}s` }}
    >
      <div className="sr-reply-top">
        <div className="sr-avatar">
          {reply.profile_image
            ? <img src={reply.profile_image} alt="" onError={e => e.target.style.display="none"} />
            : initials}
        </div>
        <div className="sr-reply-meta">
          <span className="sr-reply-username">@{reply.username || "unknown"}</span>
          {reply.location && <span className="sr-reply-loc">📍 {reply.location}</span>}
        </div>
        {isBot && <span className="sr-bot-tag">BOT</span>}
      </div>
      {reply.text && <div className="sr-reply-text">{reply.text}</div>}
    </div>
  );
}

function LogLine({ event, msg, data }) {
  if (event === "reply_saved") return null;
  const icon = EVENT_ICONS[event] || "·";
  return (
    <div className={`sr-log-line type-${event}`}>
      <span className="sr-log-icon">{icon}</span>
      <span className="sr-log-msg">{msg}</span>
      {event === "replies_page" && data?.count &&
        <span className="sr-log-badge blue">{data.count}</span>}
      {event === "users_fetch" && data?.new > 0 &&
        <span className="sr-log-badge green">+{data.new}</span>}
    </div>
  );
}

export default function ScanRoom() {
  const { mamdaniMode: mm, data: mmData } = useMamdani();
  const [politicians, setPoliticians] = useState([]);
  const [selected, setSelected]       = useState(null);
  const [scanning, setScanning]       = useState(false);
  const [logs, setLogs]               = useState([]);
  const [replies, setReplies]         = useState([]);
  const [summary, setSummary]         = useState(null);
  const [status, setStatus]           = useState("idle"); // idle | running | done | error
  const [showDivider, setShowDivider] = useState(false);

  const feedRef = useRef(null);
  const esRef   = useRef(null);

  useEffect(() => {
    if (mm) return;
    fetch(`${API}/api/politicians`)
      .then(r => r.json())
      .then(d => { if (d.success) setPoliticians(d.politicians); })
      .catch(console.error);
  }, [mm]);

  // auto scroll
  useEffect(() => {
    if (feedRef.current) {
      const el = feedRef.current;
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (isAtBottom) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [logs, replies]);

  const handleSelect = (p) => {
    if (scanning) return;
    setSelected(p);
    setLogs([]);
    setReplies([]);
    setSummary(null);
    setStatus("idle");
    setShowDivider(false);
  };

  const handleScan = () => {
    if (!selected || scanning) return;
    setScanning(true);
    setLogs([]);
    setReplies([]);
    setSummary(null);
    setStatus("running");
    setShowDivider(false);

    if (esRef.current) esRef.current.close();

    const es = new EventSource(
      `${API_SCAN}/api/targets/rescan-stream?username=${encodeURIComponent(selected.username)}`
    );
    esRef.current = es;

    es.onmessage = (e) => {
      const { event, msg, data } = JSON.parse(e.data);
      console.log("SSE event:", event);

      if (event === "reply_saved") {
        setShowDivider(true);
        setReplies(prev => [data, ...prev].slice(0, 100));
        return;
      }

      setLogs(prev => [...prev, { event, msg, data, id: Date.now() + Math.random() }]);

      if (event === "done") {
      es.close();
      setScanning(false);
      if (msg === "success") {
          setStatus("done");
          setSummary(data);
        // עדכן את הדשבורד הראשי
        fetch(`${API}/api/refresh-dashboard`).catch(() => {});
        } else {
          setStatus("error");
        }
      }
    };

    es.onerror = () => {
      es.close();
      setScanning(false);
      setStatus("error");
      setLogs(prev => [...prev, { event: "error", msg: mm ? "Connection error" : "שגיאה בחיבור לשרת", id: Date.now() }]);
    };
  };

  const handleStop = () => {
    if (esRef.current) esRef.current.close();
    setScanning(false);
    setStatus("idle");
  };

  const accounts = mm ? mmData.accounts : politicians;

  return (
    <div className="sr-root" dir={mm ? "ltr" : "rtl"}>

      <div className="sr-header">
        <h1 className="sr-title">{mm ? "Impersonation Detection" : "זיהוי התחזות"}</h1>
        <p className="sr-subtitle">{mm ? "Select a watchlist account and run a real-time scan" : "בחר פוליטיקאי והפעל סריקה בזמן אמת"}</p>
      </div>

      <div className="sr-politicians">
        {accounts.map(p => {
          const img  = p.profile_image_url || p.profile_image;
          const name = p.display_name || p.name || p.username;
          const bots = mm ? (p.bots?.length ?? 0) : (p.bot_count || 0);
          return (
            <div
              key={p.username}
              className={`sr-pol-card ${selected?.username === p.username ? "active" : ""} ${scanning ? "disabled" : ""}`}
              onClick={() => handleSelect(p)}
            >
              {img
                ? <img src={img} alt={name} className="sr-pol-img" />
                : <div className="sr-pol-img-placeholder">{name[0]}</div>
              }
              <div className="sr-pol-info">
                <span className="sr-pol-name">{name}</span>
                <span className="sr-pol-bots"> {bots}</span>
              </div>
              {selected?.username === p.username && (
                <div className="sr-pol-check">✓</div>
              )}
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="sr-scan-bar">
          <div className="sr-selected-info">
            {(selected.profile_image_url || selected.profile_image) &&
              <img src={selected.profile_image_url || selected.profile_image} alt="" className="sr-selected-img" />}
            <span className="sr-selected-name">@{selected.username}</span>
          </div>

          {!scanning ? (
            <button className="sr-btn-scan" onClick={handleScan}>
              <span className="sr-btn-icon">▶</span>
              {mm ? "Start Scan" : "התחל סריקה"}
            </button>
          ) : (
            <button className="sr-btn-stop" onClick={handleStop}>
              <span className="sr-btn-icon">■</span>
              {mm ? "Stop" : "עצור"}
            </button>
          )}
        </div>
      )}

      {(logs.length > 0 || replies.length > 0) && (
        <div className="sr-feed">

          <div className="sr-feed-header">
            <div className={`sr-feed-dot ${status}`} />
            <span className={`sr-feed-status ${status}`}>
              {status === "running" ? "LIVE" : status === "done" ? "DONE" : "ERROR"}
            </span>
            {replies.length > 0 &&
              <span className="sr-feed-badge">{replies.length} {mm ? "replies" : "תגובות"}</span>}
            {scanning &&
              <span className="sr-feed-spinner" />}
          </div>

          <div className="sr-feed-body" ref={feedRef}>
            <div className="sr-logs">
              {logs.map(l => <LogLine key={l.id} {...l} />)}
            </div>

            {showDivider && (
              <>
                <div className="sr-divider">
                  <div className="sr-divider-line" />
                  <span className="sr-divider-label">LIVE REPLIES</span>
                  <div className="sr-divider-line" />
                </div>
                <div className="sr-replies">
                  {replies.map((r, i) => (
                    <ReplyCard key={r.twitter_id || i} reply={r} index={i} />
                  ))}
                </div>
              </>
            )}
          </div>

          {summary && (
            <div className="sr-summary">
              <div className="sr-sum-item">
                <span className="sr-sum-val blue">{summary.replies ?? 0}</span>
                <span className="sr-sum-label">{mm ? "Replies" : "תגובות"}</span>
              </div>
              <div className="sr-sum-item">
                <span className="sr-sum-val red">{summary.bots ?? 0}</span>
                <span className="sr-sum-label">{mm ? "Bots" : "בוטים"}</span>
              </div>
              <div className="sr-sum-item">
                <span className="sr-sum-val green">
                  {summary.replies > 0
                    ? Math.round((summary.bots / summary.replies) * 100)
                    : 0}%
                </span>
                <span className="sr-sum-label">{mm ? "Bot Rate" : "אחוז בוטים"}</span>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}